import { Chip } from '../family-finance/shared'
import { POSITIONS } from '../../types/fantasy-football'
import { tiersByPosition, type ExportRow } from '../../lib/draft-export'
import { POSITION_TONES } from './useFantasy'

/**
 * Draft-day view: the same board regrouped by position and broken into tiers,
 * which is the shape you actually need mid-draft — "who is left at RB, and is
 * there a cliff after him" — rather than a single 60-long list.
 *
 * Also the print target. `print:` utilities collapse it to flat ink on white
 * so a paper copy stays readable.
 */
export default function DraftCheatSheet({ rows }: { rows: ExportRow[] }) {
  const tiers = tiersByPosition(rows)
  const populated = POSITIONS.filter((p) => tiers[p].length > 0)

  return (
    <div
      id="draft-cheat-sheet"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 print:grid-cols-3 print:gap-2"
    >
      {populated.map((position) => (
        <div key={position} className="break-inside-avoid">
          <div className="flex items-center gap-2 mb-2">
            <Chip tone={POSITION_TONES[position]}>{position}</Chip>
            <span className="text-slate-500 text-xs">
              {tiers[position].reduce((n, t) => n + t.rows.length, 0)} players
            </span>
          </div>

          <div className="space-y-2">
            {tiers[position].map((tier) => (
              <div
                key={tier.tier}
                className="rounded-lg border border-slate-200 dark:border-slate-800 print:border-slate-400"
              >
                <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-800 print:border-slate-400">
                  Tier {tier.tier}
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800/70 print:divide-slate-300">
                  {tier.rows.map((row) => (
                    <li
                      key={row.name + row.rank}
                      className="flex items-baseline gap-2 px-2 py-1 text-sm print:py-0.5"
                    >
                      <span className="text-slate-400 text-xs w-6 shrink-0 tabular-nums">
                        {row.rank}
                      </span>
                      <span className="text-slate-900 dark:text-white truncate print:text-black">
                        {row.name}
                      </span>
                      <span className="text-slate-500 text-xs ml-auto shrink-0 tabular-nums">
                        {row.nflTeam} · B{row.byeWeek || '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
